import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  asapScheduler,
  BehaviorSubject,
  catchError,
  combineLatest,
  combineLatestWith,
  debounceTime,
  defaultIfEmpty,
  distinctUntilChanged,
  EMPTY,
  finalize,
  first,
  from,
  map,
  mergeMap,
  Observable,
  of,
  ReplaySubject,
  scan,
  share,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
  zip,
} from 'rxjs';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import {
  EEstadoEntidadeNotificada,
  OccurrenceAttachmentModelLike,
  OccurrenceModel,
  OccurrenceModelLike,
  OccurrenceNoteModelLike,
  OccurrenceObjectModelLike,
  OccurrenceType,
  OccurrenceTypeModelLike,
  StatusOccurrenceLike,
  StatusOccurrenceType,
} from 'app/shared/models/occurrence.model';
import { ToastService } from 'app/shared/services/toast.service';
import { UploadService } from 'app/shared/services/upload.service';
import { ObjectUtils } from 'app/shared/utils/object.utils';

import SketchWidget from '@arcgis/core/widgets/Sketch';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import { influenceArea, measurement } from '../../../../default-symbols';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { typeObjectFieldName } from 'app/shared/constants/search-results.constant';
import { OccurrenceService } from 'app/shared/services/occurrence.service';
import { CodedValueItem } from 'app/shared/interfaces/coded-value-item.interface';
import { ExtractExpr, ItemExprUtils } from 'app/shared/utils/item-expr.utils';
import Graphic from '@arcgis/core/Graphic';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import {
  geomFieldsIgnored,
  systemFieldTypesIgnored,
} from 'app/shared/constants/ignore-fields.constant';
import { WidgetModalType } from 'app/shared/models/widget-modal-type';
import { AuthService } from 'app/shared/services/auth.service';
import { ScreenService } from 'app/shared/services/screen.service';
import { RoleType } from 'app/shared/models/role-type';
import { TranslationService } from '@services/translation.service';

interface OccurrenceFormLike {
  status: FormControl<string>;
  type: FormControl<string>;
  observation: FormControl<string>;
}

interface SectionListTplContext<T = any> {
  title: string;
  readonly btnAddEnabled?: boolean;
  onAddClick?: (e: any) => void;
  onDeleteClick?: (item: T, index: number) => void;
  handlePagedData: (pageIndex?: number) => void;
  onViewClick?: (item: T) => void;
  columnsCount: number;
  pageObj: {
    items?: T[];
    pageIndex: number;
    totalCount?: number;
    pageSize: number;
    onPageChange?: (pageIndex: number) => void;
  };
}

interface SectionListItemLike<T = any> {
  key: string;
  text: string;
  data: T;
}

enum SelectAreaType {
  User,
  Object,
}

const entityIdFieldName = 'identidade';
const objectRegisterIdFieldName = 'idobjeto';

@Component({
  selector: 'app-occurrence-register',
  templateUrl: 'occurrence-register.component.html',
  styleUrls: ['occurrence-register.component.scss'],
})
export class OccurrenceRegisterComponent
  implements OnInit, OnChanges, OnDestroy
{
  @ViewChild('attachmentFileInputEl', { static: true })
  attachmentFileInputElRef: ElementRef<HTMLInputElement>;

  @Input()
  mapView: __esri.MapView;
  @Input()
  mainMapImageLayer: __esri.MapImageLayer;
  @Input()
  groupLayer: __esri.GroupLayer;

  @Input()
  model: OccurrenceModelLike;
  private modelInternal: OccurrenceModelLike;

  @Output()
  onSaved = new EventEmitter<OccurrenceModelLike>();

  get isEditMode() {
    return !!this.modelInternal?.id;
  }

  formGroup: FormGroup<OccurrenceFormLike>;

  noteFormModalVisible = false;

  statusList: StatusOccurrenceLike[];
  statusMap = StatusOccurrenceType;

  typeList: OccurrenceTypeModelLike[];

  private noteList: Array<OccurrenceNoteModelLike> = [];
  private attachmentList: Array<OccurrenceAttachmentModelLike> = [];

  areaGridFields: __esri.Field[];
  areaPoint: __esri.Point;
  areaResultListToSelect: __esri.Graphic[];
  areaResultListKeysSelected: any[];
  areaResultKeyExpr: ExtractExpr<__esri.Graphic> = (item) => {
    if (!item) {
      return null;
    }

    return this.generateObjectKey(
      item.attributes[typeObjectFieldName],
      item.attributes[entityIdFieldName],
      item.attributes[objectRegisterIdFieldName]
    );
  };

  notesSectionListContext: SectionListTplContext;
  attachmentsSectionListContext: SectionListTplContext;

  private selectAreaEventsSubject = new Subject<SelectAreaType>();
  private selectAreaEventsSubscription: Subscription;
  private token: string;

  canceledModalVisible = false;
  descriptionCanceled: string;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private _modelInternalSubject = new BehaviorSubject<OccurrenceModelLike>(
    null
  );
  private _modelStatusSiiaObservable = this._modelInternalSubject.pipe(
    map((x) => {
      return x?.statusSiia;
    }),
    distinctUntilChanged()
  );
  private _isSameEntityObservable = combineLatest(
    this.authService.observeEntityId(),
    this._modelInternalSubject
  ).pipe(
    debounceTime(0, asapScheduler),
    map(([entityId, model]) => {
      return model?.entityId === entityId;
    }),
    distinctUntilChanged()
  );
  private _isEntityNotifiedCanEditObservable = combineLatest(
    this.authService.observeEntityId(),
    this._modelInternalSubject
  ).pipe(
    debounceTime(0, asapScheduler),
    map(([entityId, model]) => {
      return !!model?.notifiedEntityList?.some((x) => {
        return (
          x.id === entityId && x.status !== EEstadoEntidadeNotificada.Rejected
        );
      });
    }),
    combineLatestWith(
      this.authService.observeHasAnyRoleTypes(RoleType.OccurrencesAdmin)
    ),
    map((res) => {
      return res.every((x) => x);
    }),
    distinctUntilChanged()
  );

  private _isNewModeObservable = this._modelInternalSubject.pipe(
    map((model) => {
      return !model?.id;
    })
  );

  canEditAttachmentsObservable = combineLatest(
    this._isNewModeObservable,
    this._isSameEntityObservable
  ).pipe(
    map((res) => {
      return res.some((x) => x);
    }),
    distinctUntilChanged()
  );
  canEditNotesObservable = combineLatest(
    this._isNewModeObservable,
    this._isSameEntityObservable,
    this._isEntityNotifiedCanEditObservable,
    this.authService.observeHasAnyRoleTypes(RoleType.OccurrencesSupervisor)
  ).pipe(
    map((res) => {
      return res.some((x) => x);
    }),
    distinctUntilChanged()
  );

  canSaveObservable = combineLatest(
    this._isNewModeObservable,
    combineLatest(
      this._modelStatusSiiaObservable.pipe(
        map((statusSiia) => {
          return (
            StatusOccurrenceType.Canceled !== statusSiia &&
            StatusOccurrenceType.Resolved !== statusSiia
          );
        })
      ),
      combineLatest(
        this.canEditAttachmentsObservable,
        this.canEditNotesObservable
      ).pipe(
        map((res) => {
          return res.some((x) => x);
        })
      )
    ).pipe(
      map((res) => {
        return res.every((x) => x);
      })
    )
  ).pipe(
    debounceTime(0),
    map((res) => {
      return res.some((x) => x);
    }),
    distinctUntilChanged()
  );

  private _typeEnumSelectedSubject = new BehaviorSubject<OccurrenceType>(null);
  private _shouldSearchObjectsObservable = this._typeEnumSelectedSubject.pipe(
    map((type) => {
      return type !== OccurrenceType.ObjectOnTheGroundNotInTheSystem;
    }),
    distinctUntilChanged()
  );

  private lifecycle = new Subject<void>();

  constructor(
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private mainMapService: MainMapService,
    private uploadService: UploadService,
    private occurrenceService: OccurrenceService,
    private toastService: ToastService,
    private globalLoadingController: LoadingController,
    private screenService: ScreenService,
    private ts: TranslationService
  ) {
    this.formGroup = this.formBuilder.group({
      status: this.formBuilder.control<string>(null, [Validators.required]),
      type: this.formBuilder.control<string>(null, [Validators.required]),
      observation: this.formBuilder.control<string>(null),
    });
  }

  ngOnInit(): void {
    this.authService.getToken().subscribe((x) => {
      this.token = x;
    });

    this.notesSectionListContext =
      this.createSectionListContext<OccurrenceNoteModelLike>(
        () => this.noteList,
        (data) => {
          return { key: data.id, text: data.text, data };
        },
        {
          title: 'NOTA',
          pageSize: 4,
          onAddClick: () => {
            this.noteFormModalVisible = true;
          },
          btnAddEnabled: this.canEditNotesObservable,
        }
      );

    this.attachmentsSectionListContext =
      this.createSectionListContext<OccurrenceAttachmentModelLike>(
        () => this.attachmentList,
        (data) => {
          return { key: data.id, text: data.text, data };
        },
        {
          title: 'ANEXOS',
          pageSize: 6,
          columnsCount: 2,
          onAddClick: () => {
            this.attachmentFileInputElRef.nativeElement.click();
          },
          btnAddEnabled: this.canEditAttachmentsObservable,

          onViewClick: async (item) => {
            try {
              const token = this.token;
              const attachment = await fetch(item.url, {
                headers: { Authorization: `Bearer ${token}` },
              });

              if (!attachment.ok) {
                throw new Error(`Erro ao baixar: ${attachment.statusText}`);
              }

              const blob = await attachment.blob();
              const blobUrl = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = item.text;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              URL.revokeObjectURL(blobUrl);
            } catch (error) {
              console.log('Erro ao carregar anexo', error);

              this.toastService.error(
                this.ts.getCurrentLanguage()['failed-download-attachment']
              );
            }
          },
        }
      );

    this.globalLoadingController.show();
    this.occurrenceService
      .getOccurrenceStatusList()
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (list) => {
          this.statusList = list;
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-load-incident-statuses']
          );
        },
      });

    this.globalLoadingController.show();
    this.occurrenceService
      .getOccurrenceTypeList()
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (list) => {
          this.typeList = list;

          this.formGroup.controls.type.valueChanges
            .pipe(takeUntil(this.lifecycle))
            .subscribe((value) => {
              const type = this.typeList?.find((x) => x.id === value)?.idSiia;
              this._typeEnumSelectedSubject.next(type);
            });
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-load-incident-types']
          );
        },
      });

    this.handleModel();

    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.handleModel(true);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.mapView ||
      changes.mainMapImageLayer ||
      changes.searchGroupLayer
    ) {
      this.handleSelectAreaEvents();
    }

    if (changes.model && !changes.model.firstChange) {
      this.handleModel();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.selectAreaEventsSubscription?.unsubscribe();

    this.notesSectionListContext = null;
    this.attachmentsSectionListContext = null;
  }

  onBtnChangeStatusClick(statusSiia: StatusOccurrenceType) {
    if (this.globalLoadingController.isShown()) {
      return;
    }

    this.globalLoadingController.show();
    this.occurrenceService
      .setStatusSiia(
        this.modelInternal?.id,
        statusSiia,
        this.descriptionCanceled
      )
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (res) => {
          this.descriptionCanceled = null;
          this.canceledModalVisible = false;

          this.toastService.success(
            this.ts.getCurrentLanguage()['incident-status-updated-successfully']
          );

          this.onSaved.emit(res);
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-update-incident-status']
          );
        },
      });
  }

  onBtnClearClick() {
    this.handleModel(true);
  }

  onSubmit(e) {
    e.preventDefault();

    if (this.globalLoadingController.isShown()) {
      return;
    }

    if (this.formGroup.invalid) {
      this.toastService.error(
        this.ts.getCurrentLanguage()['fill-required-fields-correctly']
      );
      return;
    }

    if (!this.areaPoint) {
      this.toastService.error(this.ts.getCurrentLanguage()['select-area']);
      return;
    }

    const model =
      ObjectUtils.clone(this.modelInternal) ?? new OccurrenceModel();

    const changes = this.formGroup.getRawValue();
    ObjectUtils.merge(model, changes);

    model.geometry = JSON.stringify(this.areaPoint);
    model.notes = ObjectUtils.clone(this.noteList) ?? [];
    model.attachments = ObjectUtils.clone(this.attachmentList) ?? [];

    model.objects =
      this.areaResultListKeysSelected
        ?.map<OccurrenceObjectModelLike>((key) => {
          const graphic = this.areaResultListToSelect?.find((x, index) => {
            const auxKey = ItemExprUtils.extractValue(
              x,
              index,
              this.areaResultKeyExpr
            );
            return auxKey === key;
          });

          if (!graphic) {
            return null;
          }

          const type = graphic.attributes[typeObjectFieldName];
          const entityId = graphic.attributes[entityIdFieldName];
          const objectRegisterId =
            graphic.attributes[objectRegisterIdFieldName];
          return { type, entityId, objectRegisterId };
        })
        ?.filter((x) => !!x) ?? [];

    let observable: Observable<OccurrenceModel>;
    if (this.isEditMode) {
      observable = this.occurrenceService.update(model);
    } else {
      observable = this.occurrenceService.create(model);
    }

    this.globalLoadingController.show();
    observable
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (res) => {
          this.toastService.success(
            this.ts.getCurrentLanguage()['incident-saved-successfully']
          );

          if (!this.modelInternal.id) {
            this.modelInternal = new OccurrenceModel();
            this.handleModel();
          }

          this.onSaved.emit(res);
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-save-incident']
          );
        },
      });
  }

  onNoteSave(changes: Partial<OccurrenceNoteModelLike>) {
    this.noteList.push(changes);

    this.notesSectionListContext?.handlePagedData();
  }

  onAttachmentFileChange() {
    if (this.globalLoadingController.isShown()) {
      return;
    }

    const file = this.attachmentFileInputElRef.nativeElement.files?.item(0);
    if (file) {
      this.attachmentFileInputElRef.nativeElement.value = '';

      this.globalLoadingController.show();
      this.uploadService
        .uploadFile(file)
        .pipe(
          finalize(() => {
            this.globalLoadingController.hide();
          }),
          takeUntil(this.lifecycle)
        )
        .subscribe({
          next: (url) => {
            this.attachmentList.push({
              text: file.name,
              url,
            });

            this.attachmentsSectionListContext?.handlePagedData();

            this.toastService.success(
              this.ts.getCurrentLanguage()['upload-completed-successfully']
            );
          },
          error: (response) => {
            if (!response) {
              return;
            }

            this.toastService.error(
              this.ts.getCurrentLanguage()['failed-upload-file']
            );
          },
        });
    }
  }

  onBtnSelectGeometryInMapClick() {
    this.enableSelectAreaEvents();
  }

  private handleModel(clearMode = false) {
    if (!clearMode) {
      this.modelInternal =
        ObjectUtils.clone(this.model) ?? new OccurrenceModel();
    } else {
      this.modelInternal = new OccurrenceModel({
        id: this.model?.id,
      });
    }

    this._modelInternalSubject.next(this.modelInternal);

    const values = this.modelInternal ?? {};

    this.formGroup.reset(values);

    this.areaPoint = EsriUtils.geometryFromJson(
      this.modelInternal.geometry
    ) as __esri.Point;

    this.noteList = this.modelInternal?.notes?.slice() ?? [];
    this.notesSectionListContext?.handlePagedData();

    this.attachmentList = this.modelInternal?.attachments?.slice() ?? [];
    this.attachmentsSectionListContext?.handlePagedData();

    this.areaResultListKeysSelected =
      this.modelInternal?.objects?.map((x) => {
        return this.generateObjectKey(x.type, x.entityId, x.objectRegisterId);
      }) ?? [];

    this._typeEnumSelectedSubject.next(this.modelInternal?.typeSiia ?? null);

    this.selectAreaEventsSubject.next(SelectAreaType.Object);

    if (this.modelInternal?.id) {
      this.formGroup.controls.status.enable();
    } else {
      this.formGroup.controls.status.disable();
    }
  }

  private enableSelectAreaEvents() {
    this.selectAreaEventsSubject.next(SelectAreaType.User);
  }

  private handleSelectAreaEvents() {
    this.selectAreaEventsSubscription?.unsubscribe();
    if (!this.mapView || !this.mainMapImageLayer) {
      return;
    }

    const { mapView, mainMapImageLayer } = this;

    const subscription = new Subscription();

    const searchGroupLayer: __esri.LayersMixin = this.groupLayer ?? mapView.map;

    const highlightGroupLayer = new GroupLayer();
    searchGroupLayer.add(highlightGroupLayer);

    const graphicsLayer = new GraphicsLayer();
    searchGroupLayer.add(graphicsLayer);

    const drawingLayer = new GraphicsLayer();
    searchGroupLayer.add(drawingLayer);

    const sketch = new SketchWidget({
      view: this.mapView,
      layer: drawingLayer,
      creationMode: 'single',
    });

    subscription.add(
      EsriUtils.loadAllLayers(mainMapImageLayer).subscribe(() => {
        const layers = EsriUtils.getDeeperLayers(
          mainMapImageLayer
        ) as __esri.Sublayer[];

        const fieldList: __esri.Field[] = [];
        const fieldsSet = new Set<string>();

        layers.forEach((layer) => {
          const fields = layer.fields;
          fields.forEach((field) => {
            if (
              !fieldsSet.has(field.name) &&
              !geomFieldsIgnored.includes(field.name) &&
              !systemFieldTypesIgnored.includes(field.type)
            ) {
              fieldsSet.add(field.name);
              fieldList.push(field);
            }
          });
        });

        this.areaGridFields = fieldList;
      })
    );

    const _attachSearchingObjectsByGeometry = (geometry: __esri.Geometry) => {
      return new Observable((subscriber) => {
        let sublayerList = EsriUtils.getDeeperLayers(
          mainMapImageLayer
        ) as __esri.Sublayer[];

        if (this.modelInternal?.objects?.length) {
          sublayerList = sublayerList.filter((layer) => {
            const domain = layer.getFieldDomain(
              typeObjectFieldName
            ) as __esri.CodedValueDomain;

            const title = layer.title.toLowerCase();
            return !!domain?.codedValues?.some((x) => {
              const code = (x.code as string).toLowerCase?.();
              return (
                (code === title || x.name.toLowerCase() === title) &&
                this.modelInternal.objects.some((x) => {
                  const type = x.type.toLowerCase();
                  return type === title || type === code;
                })
              );
            });
          });
        }

        const layersObservable = zip(
          sublayerList.reverse().map((sublayer) => {
            const query = sublayer.createQuery();
            query.where = '1=1';

            if (this.modelInternal?.objects?.length) {
              query.where += ` and (${this.generateWhereClauseFromObjects(
                this.modelInternal.objects
              )})`;
            }

            query.geometry = geometry;
            query.outFields = ['*'];
            query.returnGeometry = true;

            return EsriUtils.queryFeatures(sublayer, query).pipe(
              mergeMap((featureSet) => {
                if (!featureSet?.features?.length) {
                  return of(null);
                }

                return of(
                  new FeatureLayer({
                    objectIdField: sublayer.objectIdField,
                    renderer: sublayer.renderer,
                    fields: featureSet.fields,
                    source: featureSet.features,
                  })
                );
              })
            );
          })
        ).pipe(
          map((layers) => layers.filter((x) => !!x)),
          defaultIfEmpty([] as __esri.FeatureLayer[])
        );

        this.globalLoadingController.show();
        subscriber.add(
          layersObservable
            .pipe(
              catchError(() => {
                this.toastService.error(
                  this.ts.getCurrentLanguage()['failed-load-data']
                );
                return EMPTY;
              }),
              finalize(() => {
                this.globalLoadingController.hide();
              }),
              mergeMap((layers) => {
                this.areaResultListToSelect = layers
                  .reverse()
                  .flatMap((layer) => layer.source.toArray());

                if (!layers.length) {
                  return EMPTY;
                }

                highlightGroupLayer.addMany(layers);

                this.globalLoadingController.show();
                return zip(
                  layers.map((layer) => from(mapView.whenLayerView(layer)))
                ).pipe(
                  finalize(() => {
                    this.globalLoadingController.hide();
                  }),
                  mergeMap((layerViews) => {
                    return new Observable((subscriber) => {
                      subscriber.add(
                        zip(
                          layerViews.map((layerView, index) => {
                            const layer = layers[index];

                            return EsriUtils.highlight(
                              layerView,
                              layer.source.toArray()
                            );
                          })
                        ).subscribe()
                      );

                      subscriber.add(() => {
                        highlightGroupLayer.removeAll();
                      });
                    });
                  })
                );
              })
            )
            .subscribe()
        );

        subscriber.add(() => {
          graphicsLayer.removeAll();

          this.areaResultListToSelect = null;
          this.areaResultListKeysSelected = [];
          this.areaPoint = null;
        });
      });
    };

    subscription.add(
      this.selectAreaEventsSubject
        .pipe(
          switchMap((type) => {
            let graphicObservable: Observable<__esri.Graphic>;
            switch (type) {
              case SelectAreaType.Object:
                if (this.areaPoint) {
                  graphicObservable = of(
                    new Graphic({ geometry: this.areaPoint })
                  );
                } else {
                  graphicObservable = EMPTY;
                }
                break;
              case SelectAreaType.User:
              default:
                this.mainMapService.minimizeWidgetModal(
                  WidgetModalType.Occurrence
                );

                const sketchCreateObservable = EsriUtils.on(
                  sketch,
                  'create'
                ).pipe(share());

                const sketchCreateCancelObservable =
                  sketchCreateObservable.pipe(
                    first((evt: __esri.SketchCreateEvent) => {
                      return evt.state === 'cancel';
                    })
                  );

                const sketchCreateCompleteObservable =
                  sketchCreateObservable.pipe(
                    first((evt: __esri.SketchCreateEvent) => {
                      return evt.state === 'complete';
                    }),
                    takeUntil(sketchCreateCancelObservable)
                  );

                graphicObservable = sketchCreateCompleteObservable.pipe(
                  finalize(() => {
                    sketch.cancel();

                    this.mainMapService.maximizeWidgetModal(
                      WidgetModalType.Occurrence
                    );
                  }),
                  map((e) => e.graphic),
                  tap((graphic) => {
                    this.areaPoint = graphic.geometry as __esri.Point;

                    this.areaResultListKeysSelected = [];

                    drawingLayer.remove(graphic);
                  })
                );

                sketch.create('point');
                break;
            }

            return combineLatest(
              graphicObservable,
              this._shouldSearchObjectsObservable
            ).pipe(
              debounceTime(0),
              switchMap(([graphic, shouldSearch]) => {
                graphic = graphic?.clone();

                return new Observable((subscriber) => {
                  if (shouldSearch) {
                    const geometry = EsriUtils.geodesicBuffer(
                      graphic.geometry,
                      15
                    );
                    graphic.geometry = geometry;

                    graphic.symbol = influenceArea.polygonFillSymbol;

                    subscriber.add(
                      _attachSearchingObjectsByGeometry(geometry).subscribe()
                    );
                  } else {
                    graphic.symbol = measurement.pointSymbolDefault;
                  }

                  graphicsLayer.add(graphic);

                  subscriber.add(() => {
                    graphicsLayer.remove(graphic);
                  });
                });
              })
            );
          })
        )
        .subscribe()
    );

    subscription.add(() => {
      searchGroupLayer.remove(highlightGroupLayer);
      highlightGroupLayer.destroy();

      searchGroupLayer.remove(graphicsLayer);
      graphicsLayer.destroy();

      searchGroupLayer.remove(drawingLayer);
      drawingLayer.destroy();

      sketch.cancel();
      sketch.destroy();
    });

    this.selectAreaEventsSubscription = subscription;
  }

  private createSectionListContext<T = any>(
    itemsFn: () => T[],
    mapItemsFn: (item: T) => SectionListItemLike<T>,
    cfg: {
      title: string;
      btnAddEnabled?: Observable<boolean>;
      onAddClick?: (handlePagedData: (pageIndex?: number) => void) => void;
      onDeleteClick?: (
        item: T,
        index: number,
        handlePagedData: (pageIndex?: number) => void
      ) => void;
      pageSize: number;
      columnsCount?: number;
      onViewClick?: (item: T) => void;
    }
  ): SectionListTplContext<SectionListItemLike<T>> {
    let _pagedData: any[];
    let _pageIndex: number = 0;
    let _totalCount: number;
    const pageSize = cfg.pageSize;
    const columnsCount = cfg.columnsCount ?? 1;

    const handlePagedData = (pageIndex: number = _pageIndex) => {
      const list = itemsFn();
      _totalCount = list?.length;

      const lastPageIndex =
        Math.ceil((list?.length ?? 0) / cfg.pageSize) - 1 || 0;
      const newPageIndex =
        lastPageIndex <= -1
          ? -1
          : Math.max(0, Math.min(pageIndex, lastPageIndex));
      _pageIndex = newPageIndex;

      const start = newPageIndex * cfg.pageSize;

      _pagedData = list?.slice(start, start + cfg.pageSize) ?? [];

      if (mapItemsFn) {
        _pagedData = _pagedData?.map(mapItemsFn);
      }
    };

    const onDeleteClick =
      cfg.onDeleteClick ??
      ((data, index, handlePagedData) => {
        const list = itemsFn();
        list?.splice(index, 1);

        handlePagedData();
      });

    let btnAddEnabled = false;

    if (cfg?.btnAddEnabled instanceof Observable) {
      cfg.btnAddEnabled.pipe(takeUntil(this.lifecycle)).subscribe((x) => {
        btnAddEnabled = x;
      });
    }

    return {
      title: cfg.title,
      get btnAddEnabled() {
        return btnAddEnabled;
      },
      onAddClick: cfg.onAddClick?.bind(cfg, handlePagedData),
      onDeleteClick: (item, index) => {
        const data = item.data;
        index += _pageIndex * pageSize;

        onDeleteClick?.(data, index, handlePagedData);
      },
      handlePagedData,
      columnsCount,
      pageObj: {
        get items() {
          if (!_pagedData) {
            handlePagedData(0);
          }

          return _pagedData;
        },
        get pageIndex() {
          return _pageIndex;
        },
        get totalCount() {
          return _totalCount;
        },
        pageSize,
        onPageChange: handlePagedData,
      },
      onViewClick: cfg.onViewClick
        ? (item) => {
            cfg.onViewClick(item.data);
          }
        : null,
    };
  }

  private generateWhereClauseFromObjects(
    objects: OccurrenceObjectModelLike[]
  ): string {
    return objects
      .map((item) => {
        return `(${entityIdFieldName}='${item.entityId}' and ${objectRegisterIdFieldName}='${item.objectRegisterId}')`;
      })
      .join(' or ');
  }

  private generateObjectKey(
    type: string,
    entityId: string,
    objectRegisterId: string
  ) {
    return [type, entityId, objectRegisterId].join('_');
  }
}
